import type { FC } from "react";
import React, { useState } from "react";
import useQuestionnaireCubitInContext from "src/state/QuestionnaireCubit/useQuestionnaireCubitInContext";
import { QuestionnaireType } from "src/state/QuestionnaireStepCubit/QuestionnaireStepCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import {
  Badge,
  CodeBlock,
  MetaItem,
  MetaLabel,
  MetaValue,
  Section,
  SectionContent,
  SectionHeader,
  SectionTitle,
  StepCount,
  StepItem,
  StepMeta,
  StepTitle,
  Toggle,
  Toolbar
} from "src/ui/components/CustomQuestionnairePreview/debugPanel.styles";
import Link from "src/ui/components/Link/Link";

const formatPlaceholders = (text: string): string => {
  return text
    .replace(/{{var:/gm, "@")
    .replace(/{{field:/gm, "@")
    .replace(/{{hidden:/gm, "@")
    .replace(/}}/gm, "");
};

interface SectionProps {
  open: boolean;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
}

const CustomQuestionnairePreviewSteps: FC<{ sectionProps: SectionProps }> = ({
  sectionProps
}) => {
  const [
    { fields, logicSteps, activeField, customFormData },
    { parseAnswerOnlyValue, isFinishStep }
  ] = useQuestionnaireCubitInContext();
  // customFormData is destructured to trigger re-renders when answers change
  void customFormData;
  const activeRef = activeField?.ref;

  const [autoSkip, setAutoSkip] = useState(
    StorageController.getItem("debug.autoSkip") === "true"
  );
  const [hideUnused, setHideUnused] = useState(true);

  const isSupportedType = (itemType: string): boolean => {
    const values = Object.values(QuestionnaireType);
    return Object.keys(QuestionnaireType)
      .map((_, i) => values[i] as string)
      .includes(itemType);
  };

  const getAnswerValue = (id: string): string => {
    const { value } = parseAnswerOnlyValue(id);
    return value !== undefined ? JSON.stringify(value, null, 2) : "--";
  };

  const toggleAutoSkip = (): void => {
    const newValue = !autoSkip;
    StorageController.setItem("debug.autoSkip", newValue ? "true" : "false");
    setAutoSkip(newValue);
  };

  const visibleFields = fields.filter((f) =>
    hideUnused ? logicSteps.includes(f.ref) : true
  );

  return (
    <Section {...sectionProps}>
      <SectionHeader>
        <SectionTitle>
          Steps
          <Badge>
            {logicSteps.length}/{fields.length}
          </Badge>
        </SectionTitle>
      </SectionHeader>
      <SectionContent>
        <Toolbar>
          <Toggle>
            <input
              type="checkbox"
              checked={autoSkip}
              onChange={toggleAutoSkip}
            />
            Auto-Skip
          </Toggle>
          <Toggle>
            <input
              type="checkbox"
              checked={hideUnused}
              onChange={() => setHideUnused(!hideUnused)}
            />
            Hide Unused
          </Toggle>
        </Toolbar>

        <StepCount>
          Showing <strong>{visibleFields.length}</strong> of {fields.length}{" "}
          steps
        </StepCount>

        {visibleFields.map((item) => {
          const isActive = activeRef === item.ref;
          const isFinish = isFinishStep(item);
          const isUnused = !logicSteps.includes(item.ref);

          return (
            <StepItem
              key={item.id}
              data-active={isActive}
              data-finish={isFinish}
              data-unused={isUnused}
            >
              <Link to={`#${item.ref}`}>
                <StepTitle>{formatPlaceholders(item.title)}</StepTitle>
              </Link>
              <StepMeta>
                <MetaItem>
                  <MetaLabel>TYPE</MetaLabel>
                  <MetaValue>{item.type}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>ID</MetaLabel>
                  <MetaValue>{item.id}</MetaValue>
                </MetaItem>
                <MetaItem>
                  <MetaLabel>REF</MetaLabel>
                  <MetaValue>{item.ref}</MetaValue>
                </MetaItem>
              </StepMeta>
              {isSupportedType(item.type) && (
                <CodeBlock>{getAnswerValue(item.id)}</CodeBlock>
              )}
            </StepItem>
          );
        })}
      </SectionContent>
    </Section>
  );
};

export default CustomQuestionnairePreviewSteps;
